import { bind } from 'bind-event-listener';

type ExponentialRetryOptions<T> = {
	fn: () => Promise<T>;
	maxRetries?: number;
	retryCount?: number;
	delay?: number;
	/** When true, adds jitter: the actual delay is random between minDelay and the computed exponential delay */
	jitter?: boolean;
	/** Minimum delay in milliseconds before retry (default: 500) */
	minDelay?: number;
	/** When true, pauses retries while the browser is offline and resumes when connectivity returns */
	pauseWhileOffline?: boolean;
	/** Predicate which determines if the error should be retried */
	errorFilter?: (error: any) => boolean;
	/** AbortSignal to cancel the retry during backoff wait */
	signal?: AbortSignal;
	/** Callback invoked before each retry attempt (not called for the initial attempt) */
	onRetry?: (attempt: number, error: any) => void;
};

/**
 * Abort-aware sleep that resolves after `ms` or rejects early if the signal is aborted.
 * Falls back to a regular setTimeout when no signal is provided.
 */
const abortableSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			return;
		}

		let cleanup: () => void = () => {};
		const timer = setTimeout(() => {
			cleanup();
			resolve();
		}, ms);

		if (signal) {
			const onAbort = () => {
				clearTimeout(timer);
				cleanup();
				reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			};
			cleanup = bind(signal, {
				type: 'abort',
				listener: onAbort,
				options: { once: true },
			});
		}
	});

/**
 * Resolves when the browser reports it is online.
 * Returns immediately if already online, otherwise waits for the 'online' event.
 * Rejects if the signal is aborted while waiting.
 */
const waitForOnline = (signal?: AbortSignal): Promise<void> => {
	if (typeof navigator === 'undefined' || navigator.onLine) {
		return Promise.resolve();
	}

	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			return;
		}

		const onOnline = () => {
			cleanup();
			resolve();
		};
		const onAbort = () => {
			cleanup();
			reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
		};

		const unbindOnline = bind(window, {
			type: 'online',
			listener: onOnline,
			options: { once: true },
		});

		const unbindAbort = signal
			? bind(signal, {
					type: 'abort',
					listener: onAbort,
					options: { once: true },
				})
			: () => {};

		const cleanup = () => {
			unbindOnline();
			unbindAbort();
		};
	});
};

/** Retry the function using exponential backoff */
export const exponentialRetry = async <T>({
	fn,
	maxRetries = 3,
	retryCount = 0,
	delay = 1000,
	jitter = false,
	minDelay = 500,
	pauseWhileOffline = false,
	errorFilter,
	signal,
	onRetry,
}: ExponentialRetryOptions<T>): Promise<T> => {
	try {
		return await fn();
	} catch (error) {
		if (errorFilter && !errorFilter(error)) {
			throw error;
		}

		if (retryCount >= maxRetries) {
			throw error;
		}

		onRetry?.(retryCount + 1, error);

		if (pauseWhileOffline) {
			await waitForOnline(signal);
		}

		const effectiveDelay = jitter ? Math.max(minDelay, Math.random() * delay) : delay;
		await abortableSleep(effectiveDelay, signal);

		return await exponentialRetry({
			fn,
			maxRetries,
			retryCount: retryCount + 1,
			delay: delay * 2,
			jitter,
			minDelay,
			pauseWhileOffline,
			errorFilter,
			signal,
			onRetry,
		});
	}
};
